class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(
        this.dataset.index,
        0,
        undefined,
        event.target.closest('button, a')
      );
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  static latestRequestId = 0;

  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    if (!this.isConnected || !this.contains(event.target)) return;
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement?.getAttribute('name')
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__empty',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__header',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart__footer-check-drawer-shipping',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__footer',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.cart__footer-check-shipping',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.cart__blocks',
      },
    ];
  }

  async updateQuantity(line, quantity, name, control = document.activeElement) {
    if (!this.isConnected) return;
    const requestId = ++CartItems.latestRequestId;
    // A cart page can exist without the optional drawer. Only ask for sections
    // whose containers are mounted in this page.
    const sections = this.getSectionsToRender().filter((section) =>
      document.getElementById(section.id)
    );
    const focusState = {
      control,
      id: control?.id,
      name: control?.getAttribute('name') || name,
      removeId: control?.closest('cart-remove-button')?.id,
      removeIndex: Array.from(
        control?.closest('.cart-item')?.querySelectorAll('cart-remove-button') || []
      ).indexOf(control?.closest('cart-remove-button')),
      withinCart: this.contains(control),
    };
    const loadingState = this.enableLoading(line, requestId);
    const isCurrent = () =>
      this.isConnected && requestId === CartItems.latestRequestId;

    try {
      const body = JSON.stringify({
        line,
        quantity,
        sections: [...new Set(sections.map((section) => section.section))],
        sections_url: window.location.pathname,
      });
      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body,
      });
      const state = await response.text();
      if (!isCurrent()) return;
      const parsedState = JSON.parse(state);
      if (!response.ok || parsedState.errors || parsedState.status ||
        typeof parsedState.item_count !== 'number') {
        throw new Error('Cart change failed');
      }

      // Validate and prepare every fragment before changing visible state. A
      // partial section response must not leave a half-updated cart behind.
      const updates = this.prepareSectionUpdates(parsedState.sections, sections);
      const activeElement = document.activeElement;
      const restoreFocus =
        focusState.withinCart &&
        (activeElement === document.body || activeElement === control);
      const isEmpty = parsedState.item_count === 0;
      const cartDrawer = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');
      const cartItems = document.querySelector('cart-items');
      document.body.classList.toggle('cart-empty', isEmpty);
      cartFooter?.classList.toggle('is-empty', isEmpty);
      cartDrawer?.classList.toggle('is-empty', isEmpty);
      cartItems?.classList.toggle('is-empty', isEmpty);

      updates.forEach(({ target, fragment }) => target.replaceChildren(fragment));
      this.updateLiveRegions(line, parsedState.item_count);
      if (restoreFocus) this.restoreControlFocus(line, focusState, isEmpty);
      window.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: parsedState,
        })
      );
      return parsedState;
    } catch {
      if (!isCurrent()) return;
      const drawer = this.closest('cart-drawer');
      const errors = drawer
        ? this.querySelector('#CartDrawer-CartErrors')
        : document.getElementById('cart-errors');
      if (errors) errors.textContent = window.cartStrings.error;
      if (
        focusState.withinCart &&
        document.activeElement === document.body &&
        control?.isConnected &&
        (!drawer || drawer.classList.contains('active'))
      ) {
        control.focus();
      }
    } finally {
      this.disableLoading(loadingState);
    }
  }

  prepareSectionUpdates(sectionHTML, sections) {
    const documents = new Map();
    const updates = sections.map((section) => {
      if (!documents.has(section.section)) {
        const html = sectionHTML?.[section.section];
        if (typeof html !== 'string') {
          throw new Error(`Missing cart section: ${section.section}`);
        }
        documents.set(
          section.section,
          new DOMParser().parseFromString(html, 'text/html')
        );
      }
      const root = document.getElementById(section.id);
      const target = root?.querySelector(section.selector) || root;
      const source = documents.get(section.section).querySelector(section.selector);
      if (!target || !source) {
        throw new Error(`Missing cart fragment: ${section.selector}`);
      }
      return { target, source, section: section.section };
    });

    // The cart footer requests both .js-contents and its .cart__blocks parent.
    // Keep only the parent update before moving nodes out of the parsed document.
    return updates
      .filter((update, index) =>
        !updates.some((other, otherIndex) =>
          index !== otherIndex &&
          other.section === update.section &&
          ((other.target === update.target && otherIndex < index) ||
            (other.target !== update.target &&
              other.target.contains(update.target) &&
              other.source.contains(update.source)))
        )
      )
      .map(({ target, source }) => {
        const fragment = document.createDocumentFragment();
        fragment.append(...source.childNodes);
        return { target, fragment };
      });
  }

  restoreControlFocus(line, focusState, isEmpty) {
    const drawer = this.closest('cart-drawer');
    // A response arriving after the customer closes the drawer must not put
    // keyboard focus back inside it.
    if (drawer && !drawer.classList.contains('active')) return;
    const prefix = drawer ? 'CartDrawer-Item-' : 'CartItem-';
    const lineItem = this.querySelector(`[id="${prefix}${line}"]`);
    let target = focusState.id ? document.getElementById(focusState.id) : null;
    if (!this.contains(target)) target = null;
    if (!target && lineItem && focusState.name) {
      target = Array.from(lineItem.querySelectorAll('[name]')).find(
        (element) => element.getAttribute('name') === focusState.name
      );
    }
    if (!target && lineItem && focusState.removeId) {
      // Cart-page rows have separate desktop/mobile remove controls. Preserve
      // that position without forcing layout to test which duplicate is visible.
      target = lineItem.querySelectorAll('cart-remove-button')[focusState.removeIndex]
        ?.querySelector('button, a');
    }
    if (!target) {
      target = isEmpty
        ? (drawer || this).querySelector('.drawer__empty a, .cart__warnings a, .drawer__close')
        : this.querySelector('.cart-item__name, input[name="updates[]"]');
    }
    if (!target) return;
    if (drawer) {
      trapFocus(
        (isEmpty && drawer.querySelector('.drawer__inner-empty')) ||
        drawer.querySelector('.drawer__inner') || drawer,
        target
      );
    } else {
      target.focus();
    }
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      for (const prefix of ['', 'CartDrawer-']) {
        const error = document.getElementById(
          prefix ? `${prefix}LineItemError-${line}` : `Line-item-error-${line}`
        );
        const quantity = document.getElementById(
          prefix ? `Drawer-quantity-${line}` : `Quantity-${line}`
        );
        const message = error?.querySelector('.cart-item__error-text');
        if (message && quantity) {
          message.textContent = window.cartStrings.quantityError.replace(
            '[quantity]', quantity.value
          );
        }
      }
    }
    // Either surface can update both carts, so keep their count baselines in sync.
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((items) => {
      items.currentItemCount = itemCount;
    });
    const status = this.querySelector(
      '#shopping-cart-line-item-status, #CartDrawer-LineItemStatus'
    );
    status?.setAttribute('aria-hidden', true);
    const cartStatus = this.querySelector('#CartDrawer-LiveRegionText') ||
      document.getElementById('cart-live-region-text');
    cartStatus?.setAttribute('aria-hidden', false);
    clearTimeout(this.liveRegionTimeout);
    this.liveRegionTimeout = setTimeout(() => {
      cartStatus?.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line, requestId) {
    const container = this.querySelector('#main-cart-items, #CartDrawer-CartItems');
    const overlays = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay, #CartDrawer-Item-${line} .loading-overlay`
    );
    if (container) {
      container.cartUpdateRequestId = requestId;
      container.classList.add('cart__items--disabled');
    }
    overlays.forEach((overlay) => overlay.classList.remove('hidden'));
    if (this.contains(document.activeElement)) document.activeElement.blur();
    const status = this.querySelector(
      '#shopping-cart-line-item-status, #CartDrawer-LineItemStatus'
    );
    status?.setAttribute('aria-hidden', false);
    return { container, overlays, status, requestId };
  }

  disableLoading({ container, overlays, status, requestId }) {
    // A stale response must not clear the pending state of a newer request.
    if (container && container.cartUpdateRequestId !== requestId) return;
    if (container) {
      delete container.cartUpdateRequestId;
      container.classList.remove('cart__items--disabled');
    }
    overlays.forEach((overlay) => overlay.classList.add('hidden'));
    status?.setAttribute('aria-hidden', true);
  }

}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, 300)
        );
      }
    }
  );
}
